<script setup>
import { ref, computed, onBeforeUnmount, watch, onBeforeMount } from 'vue'
import { useProfileStore } from '@/stores/profile'
import { useTranslaterStore } from '@/stores/translater'
import { useTripsCarStore } from '@/stores/trips'
import { useTrackStore } from '@/stores/track'
import { useLoadYMapScript } from '@/composables/useLoadYMapScript.js'
import Modal from '@/components/UTAModal.vue'
import UTACarTripsMap from '@/components/car/UTACarTripsMap.vue'
import checkArrayIncludes from '@/helpers/checkArrayIncludes'
import calcDatas from '@/helpers/calcDatas'
import driveAssessment from '@/helpers/driveAssessment'

const { mapLoader, mapCrash, mapReady } = useLoadYMapScript()

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

const profileStore    = useProfileStore()
const translaterStore = useTranslaterStore()
const tripsCarStore   = useTripsCarStore()
const trackStore      = useTrackStore()

const profile                = computed(() => profileStore.profile)
const getTranslate           = computed(() => translaterStore.getTranslate)
const tripsCarProcess        = computed(() => tripsCarStore.tripsCarProcess)
const tripsCarMsg            = computed(() => tripsCarStore.tripsCarMsg)
const track                  = computed(() => trackStore.track)
const trackMsg               = computed(() => trackStore.trackMsg)
const trackProcess           = computed(() => trackStore.trackProcess)
const UTACarTripsMaxDateFrom = computed(() => calcDatas('tm', new Date().getTime()))
const UTACarTripsMaxDateTill = computed(() => calcDatas('tEnd'))
const coords                 = computed(() => {
    let coordinates = []

    if(track.value?.length) {
        track.value.forEach(item => coordinates.push([item.coords.lon, item.coords.lat]))
    }

    return coordinates
})

const tripsCar        = ref(null)
const trip            = ref(null)
const UTACarTripsFrom = ref(calcDatas('month'))
const UTACarTripsTill = ref(calcDatas('tEnd'))
const pageNumber      = ref(1)
const isShowTripsMap  = ref(false)
const isWithMileage   = ref(true)

function dateToTimestamp(data) {
    return Math.round(new Date(data).getTime()) / 1000
}

// Получение поездок
function loadTripsCar(params, action) {

    let basicParams = {
        uuid_car: props.uuid,
        'c.limit': 10,
        'c.order': 'DESC',
        start: dateToTimestamp(UTACarTripsFrom.value),
        stop: dateToTimestamp(UTACarTripsTill.value),
        with_milage: isWithMileage.value
    }

    if(params) basicParams = {...basicParams, ...params}

    tripsCarStore.readTripsCar(basicParams).then(trips => {
        tripsCar.value = trips

        if(action) action()
    })
}

function changePage(cursor, dir) {

    const params = {
        'c.direction': dir,
        'c.cursor': cursor
    }

    loadTripsCar(params, () => {
        if(tripsCar.value?.array?.length) {
            dir == 'after' ? 
                pageNumber.value++ : 
                pageNumber.value--
        }
    })
}

function carTripsSubmit() {

    const from = dateToTimestamp(UTACarTripsFrom.value)
    const till = dateToTimestamp(UTACarTripsTill.value)

    if(from && till) {

        if(from < till) {

            if(till - from < 2764800) {

                loadTripsCar(null, () => {
                    if(tripsCarMsg.value.success) {
                        pageNumber.value = 1
                    }
                })

            } else {
                tripsCarStore.setTripsCarMsg({ success: false, text: 'CAR_EVENTS_ERROR_MAX_PERIOD' })
            }

        } else {
            tripsCarStore.setTripsCarMsg({ success: false, text: 'CAR_EVENTS_ERROR_INVALID_LIMITS' })
        }

    } else {
        tripsCarStore.setTripsCarMsg({ success: false, text: 'CAR_EVENTS_ERROR_NO_LIMITS' })
    }
}

async function showTripsMap(item) {

    trip.value = {
        startedAt: item.startedAt,
        endedAt: item.endedAt
    }
    
    isShowTripsMap.value = true

    await trackStore.readTrack({ 
        vld: true,
        car: props.uuid,
        start: item.startedAt,
        stop: item.endedAt,
        key_array: ['not_set']
    })

    trip.value.coords = coords.value
}

// Закрыть карту
function hideTripsMap() {
    isShowTripsMap.value = false
    trip.value = null
}

// Длительность поездки из сек в чч:мм:сс
function getTimeFromSeconds(secs) {
    const pad = val => ('0' + val).slice(-2);
    return [Math.floor(secs / 3600), Math.floor((secs % 3600) / 60), secs % 60]
        .map(pad)
        .join(':')
}

// Средний расход на 100км
function getFuelConsumption(mileage, fuel) {
    let result

    if(mileage == 0) result = '-'

    if(mileage && fuel) {
        result = (fuel / (mileage * 10)).toFixed(1)

        if(result > 99.0) result = 99.9

        result += ' ' + getTranslate.value.UNIT_LITER_100_KM
    }

    return result
}

// Средняя скорость
function getAverageSpeed(mileage, time) {
    let result = '-'

    if(mileage && time) {
        result = (mileage / (time / 3600)).toFixed(2).replace(/.$/,'') + ' ' + getTranslate.value.UNIT_KMH
    }

    return result
}

// Оценка манеры воздения
function calcDriveAssessmentTrip(acceleration, braking, mileage) {
    const calc = driveAssessment(acceleration, braking, mileage)

    return calc !== false ? calc : '-'
}

onBeforeMount(() => {

    if(localStorage.getItem('UTA_TRIPS_MILEAGE')) {
        
        if(isWithMileage.value !== JSON.parse(localStorage.getItem('UTA_TRIPS_MILEAGE'))) {

            isWithMileage.value = JSON.parse(localStorage.getItem('UTA_TRIPS_MILEAGE'))
        } else {

            loadTripsCar()
        }     

    } else {

        localStorage.setItem('UTA_TRIPS_MILEAGE', isWithMileage.value)

        loadTripsCar()
    }
})

onBeforeUnmount(() => {

    trackStore.setTrackMsg({})
    tripsCarStore.setTripsCarMsg({})
})

watch(isWithMileage, (newVal) => {

    localStorage.setItem('UTA_TRIPS_MILEAGE', newVal)

    loadTripsCar()

    pageNumber.value = 1
})
</script>

<template>

    <div class="uta-car-trips">

        <header class="page__header uta-header" role="heading" aria-level="1">
            <h2 class="uta-header__title">{{ getTranslate.CAR_TRIPS }}</h2>
            <div class="spinner-border header__spinner" role="status" v-if="tripsCarProcess && tripsCar?.array?.length">
                <span class="visually-hidden">Loading...</span>
            </div>
        </header>

        <form class="mb-4 uta-car-trips__form" @submit.prevent="carTripsSubmit()">
            <div class="row">
                <div class="form-group col-12 col-sm-6 col-md-4 col-xxl-3 mb-2 mb-md-4">
                    <label for="UTACarTripsFrom">{{ getTranslate.CAR_TRIPS_STARTEDAT }}:</label>
                    <input 
                        class="uta-input uta-input--block" 
                        type="datetime-local" 
                        id="UTACarTripsFrom"
                        :max="UTACarTripsMaxDateFrom"
                        v-model="UTACarTripsFrom"
                    >
                </div>
                <div class="form-group col-12 col-sm-6 col-md-4 col-xxl-3 mb-2 mb-md-4">
                    <label for="UTACarTripsTill">{{ getTranslate.CAR_TRIPS_ENDEDAT }}:</label>
                    <input 
                        class="uta-input uta-input--block" 
                        type="datetime-local" 
                        id="UTACarTripsTill"
                        :max="UTACarTripsMaxDateTill"
                        v-model="UTACarTripsTill"
                    >
                </div>
                <div class="col-md-4 col-xxl-6 align-self-center mb-4 mb-md-0">
                    <div class="d-flex justify-content-md-end align-items-center switcher switcher--text-md-left">
                        <input class="switcher__input 111uta-cars__filter-input" type="checkbox" id="UTAMileageFilter" v-model="isWithMileage" :checked="isWithMileage">
                        <label class="switcher__label 111uta-cars__filter-label" for="UTAMileageFilter">
                            <span class="switcher__text">{{ getTranslate.CAR_TRIPS_WITH_MILEAGE }}</span>
                        </label>
                    </div>    
                </div>
                <div class="col-12 col-sm-6 col-md-4 col-xxl-3">
                    <button type="submit" class="uta-btn uta-btn--main uta-btn--block uta-btn--lg uta-car-trips__submit" :disabled="tripsCarProcess">
                        <template v-if="!tripsCarProcess">
                            {{ getTranslate.BTN_REQUEST }}
                        </template>
                        <template v-else>
                            <div class="spinner-border" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </template>
                    </button>
                </div>
            </div>
        </form>

        <div 
            v-if="'success' in tripsCarMsg && !tripsCarMsg.success"
            class="my-4 uta-alert uta-alert--danger uta-car-trips__message" 
        >
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate[tripsCarMsg.text] || tripsCarMsg.text }}
            </div>
        </div>

        <template v-if="tripsCar?.array?.length">
            <div class="container-fluid">
                <div class="uta-list uta-car-trips__list">
                    <div class="uta-list__title d-none d-md-block uta-cars__title">
                        <div class="row">
                            <div class="col-6 col-xxl-4">{{ getTranslate.CAR_TRIP }}</div>
                            <div class="col-2 col-xxl-3">{{ getTranslate.CAR_TRIPS_STARTEDAT }}</div>
                            <div class="col-2 col-xxl-3">{{ getTranslate.CAR_TRIPS_ENDEDAT }}</div>
                        </div>
                    </div>

                    <div class="uta-list__content">
                        <div 
                            class="row align-items-center uta-list__item uta-car-trips__trip" 
                            :class="{ 'uta-list__item--dark' : index % 2 }" 
                            v-for="(item, index) in tripsCar.array" 
                            :key="item.createdAt"
                        >
                            <div class="col-12 col-md-6 col-xxl-4">
                                <div class="uta-car-trip">
                                    <div class="uta-car-trip-item">
                                        <div class="uta-car-trip-item__type">{{ getTranslate.CAR_TRIPS_MILEAGE }}:</div>
                                        <div class="uta-car-trip-item__value">{{ item.mileage + ' ' + getTranslate.UNIT_KM }} </div>
                                    </div>
                                    <div class="uta-car-trip-item">
                                        <div class="uta-car-trip-item__type">{{ getTranslate.CAR_TRIPS_TIME }}:</div>
                                        <div class="uta-car-trip-item__value">{{ getTimeFromSeconds(item.time) }}</div>
                                    </div>
                                    <div class="uta-car-trip-item">
                                        <div class="uta-car-trip-item__type">{{ getTranslate.STATE_COMMON_AVERAGE_SPEED }}:</div>
                                        <div class="uta-car-trip-item__value">{{ getAverageSpeed(item.mileage, item.time) }}</div>
                                    </div>
                                    <div class="uta-car-trip-item">
                                        <div class="uta-car-trip-item__type">{{ getTranslate.STATE_COMMON_FUEL_CONSUMPTION }}:</div>
                                        <div class="uta-car-trip-item__value">{{ getFuelConsumption(item.mileage, item.fuelConsumption) }}</div>
                                    </div>
                                    <div class="uta-car-trip-item">
                                        <div class="uta-car-trip-item__type">{{ getTranslate.CAR_TRIPS_DRIVING_ASSESSMENT }}:</div>
                                        <div class="uta-car-trip-item__value">{{ calcDriveAssessmentTrip(item.acceleration, item.braking, item.mileage) }}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-2 col-xxl-3">
                                <div class="uta-car-trips__trip-date">
                                    <span class="uta-car-trips__trip-label d-block d-md-none">{{ getTranslate.CAR_TRIPS_STARTEDAT }}:</span>
                                    {{ new Date(item.startedAt * 1000).toLocaleString() }}
                                </div>
                            </div>
                            <div class="col-12 col-md-2 col-xxl-3">
                                <div class="uta-car-trips__trip-date">
                                    <span class="uta-car-trips__trip-label d-block d-md-none">{{ getTranslate.CAR_TRIPS_ENDEDAT }}:</span>
                                    {{ new Date(item.endedAt * 1000).toLocaleString() }}
                                </div>
                            </div>
                            <div class="col-12 col-sm-6 col-md-2" v-if="checkArrayIncludes(profile.attr?.array, ['GEODATA', 'SUPER_ADMIN'])">
                                <div class="uta-car-trips__trip-map">
                                    <button class="uta-btn uta-btn--block" @click="showTripsMap(item)">{{ getTranslate.CAR_EVENTS_MAP }}</button>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <div class="uta-cars__pagination mt-5" v-if="tripsCar.c && (tripsCar.c.isBefore || tripsCar.c.isAfter)">
                <div class="row">
                    <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2">
                        <div class="uta-pagination">
                            <button 
                                :disabled="tripsCarProcess || !tripsCar.c.isBefore"
                                class="uta-btn uta-btn--sm uta-pagination__prev" 
                                @click.prevent="changePage(tripsCar.c.start, 'before')"
                            >
                                <span class="material-icons">
                                    navigate_before
                                </span>
                            </button>
                            <div class="uta-pagination__current">
                                {{ pageNumber }}
                            </div>
                            <button 
                                :disabled="tripsCarProcess || !tripsCar.c.isAfter"
                                class="uta-btn uta-btn--sm uta-pagination__next" 
                                @click.prevent="changePage(tripsCar.c.stop, 'after')"
                            >
                                <span class="material-icons">
                                    navigate_next
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </template>

        <transition name="bounce">
            <Modal 
                v-if="isShowTripsMap"
                :header="getTranslate.CAR_EVENTS_MAP"
                size="lg"
                @closeModal="hideTripsMap"
            >
                <template v-slot:body>
                    <div v-if="mapLoader || trackProcess" class="uta-trips-map-wrapper w-100 d-flex justify-content-center align-items-center">
                        <div class="spinner-border text-secondary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                    <div v-if="!mapLoader && !trackProcess" class="uta-trips-map-wrapper">
                        <div class="uta-trips-map-crash" v-if="mapCrash">
                            <div>{{ getTranslate.CARMAP_CRASH }}</div>
                        </div>
                        <UTACarTripsMap v-if="mapReady && trip.coords?.length" :trip="trip" />
                        <div v-else-if="trackMsg.success == false" class="h-100 d-flex justify-content-center align-items-center">
                            {{ getTranslate[trackMsg.text] }}
                        </div>
                    </div>
                </template>
            </Modal>
        </transition>

    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/car/car-trips.scss';
</style>